import {
  AdvancedFilterEntry,
  DateRangeValue,
  FilterValue,
  KanbanCheckFilter,
  KanbanDateFilter,
  KanbanFilter,
  KanbanMultiSelectFilter,
  KanbanPropertyFilter,
  KanbanSelectFilter,
  KanbanTextFilter,
  Operator,
  OPERATOR_SYMBOLS,
  PropertyValue,
} from './kanban-filters.model'

// ── Type guards ───────────────────────────────────────────────────────────────

export function isDateRangeValue(v: FilterValue): v is DateRangeValue {
  return v !== null && typeof v === 'object' && 'start' in v
}

export function isStringArray(v: FilterValue): v is string[] {
  return Array.isArray(v)
}

// ── resolveInitialValue ───────────────────────────────────────────────────────

type Resolver = (f: KanbanFilter) => FilterValue

const RESOLVERS: Record<KanbanFilter['type'], Resolver> = {
  check: f => ((f as KanbanCheckFilter).defaultActive ? (f as KanbanCheckFilter).value : null),
  select: f => (f as KanbanSelectFilter).defaultValue ?? null,
  date: f => (f as KanbanDateFilter).defaultValue ?? null,
  text: f => (f as KanbanTextFilter).defaultValue ?? null,
  multiselect: f => (f as KanbanMultiSelectFilter).defaultValue ?? null,
  property: f => {
    const pf = f as KanbanPropertyFilter
    return pf.defaultValue ? { operator: pf.defaultValue.operator, value: pf.defaultValue.value } : null
  },
}

export function resolveInitialValue(filter: KanbanFilter): FilterValue {
  return RESOLVERS[filter.type](filter)
}

// ── Query param parsing ───────────────────────────────────────────────────────

function parseRangePair(raw: string[]): DateRangeValue | null {
  const gte = raw.find(v => v.startsWith('gte.'))
  const lte = raw.find(v => v.startsWith('lte.'))
  return gte && lte ? { start: gte.slice(4), end: lte.slice(4) } : null
}

/**
 * Parses a standard filter query param back to FilterValue.
 * Returns null only for unrecognized array params (caller keeps the default).
 */
export function parseStandardQueryParam(
  raw: string | string[],
  filterType?: KanbanFilter['type']
): FilterValue {
  if (Array.isArray(raw)) return parseRangePair(raw)
  const dot = raw.indexOf('.')
  if (dot === -1) return raw
  const op = raw.slice(0, dot)
  const val = raw.slice(dot + 1)
  if (!(op in OPERATOR_SYMBOLS) || !val) return raw
  if (filterType === 'multiselect') return val.split(',').filter(Boolean)
  if (filterType === 'property') return { operator: op as Operator, value: val }
  return val
}

/**
 * Parses an advanced filter query param back to AdvancedFilterEntry.
 * Returns null for unrecognized or malformed values.
 */
export function parseAdvancedQueryParam(raw: string | string[]): AdvancedFilterEntry | null {
  if (Array.isArray(raw)) {
    const range = parseRangePair(raw)
    if (!range) return null
    return { operator: 'gte', value: range.start, rangeEnd: range.end }
  }
  const dot = raw.indexOf('.')
  if (dot === -1) return null
  const op = raw.slice(0, dot)
  const val = raw.slice(dot + 1)
  if (!(op in OPERATOR_SYMBOLS) || !val) return null
  return { operator: op as Operator, value: val }
}

// ── serializeFilterValue ──────────────────────────────────────────────────────

function operatorOf(filter: KanbanFilter): Operator {
  return ('operator' in filter && filter.operator) ? filter.operator : 'eq'
}

type Serializer = (filter: KanbanFilter, value: FilterValue) => string | null

const SERIALIZERS: Record<KanbanFilter['type'], Serializer> = {
  check: (f, v) => (v ? `${operatorOf(f)}.${v}` : null),
  select: (f, v) => (v ? `${operatorOf(f)}.${v}` : null),
  text: (f, v) => (v ? `${operatorOf(f)}.${v}` : null),
  date: (f, v) => {
    if (isDateRangeValue(v)) return `range.${v.start}_${v.end}`
    return v ? `${operatorOf(f)}.${v}` : null
  },
  property: (f, v) => {
    const pv = v as PropertyValue
    return pv?.value ? `${pv.operator}.${pv.value}` : null
  },
  multiselect: (f, v) => {
    const arr = v as string[]
    return arr?.length ? `eq.${arr.join(',')}` : null
  },
}

export function serializeFilterValue(filter: KanbanFilter, value: FilterValue): string | null {
  return SERIALIZERS[filter.type](filter, value)
}

// ── buildFilterEntries ────────────────────────────────────────────────────────

type FilterEntry = { field: KanbanFilter['field']; operator: Operator; value: string }
type EntryBuilder = (
  filter: KanbanFilter,
  value: FilterValue,
  allOptions: { value: string }[]
) => FilterEntry[]

const ENTRY_BUILDERS: Record<KanbanFilter['type'], EntryBuilder> = {
  check: (f, v) => [{ field: f.field, operator: operatorOf(f), value: v as string }],
  select: (f, v) => [{ field: f.field, operator: operatorOf(f), value: v as string }],
  text: (f, v) => [{ field: f.field, operator: operatorOf(f), value: v as string }],
  date: (f, v) => {
    if (isDateRangeValue(v)) {
      return [
        { field: f.field, operator: 'gte', value: v.start },
        { field: f.field, operator: 'lte', value: v.end },
      ]
    }
    return [{ field: f.field, operator: operatorOf(f), value: v as string }]
  },
  property: (f, v) => {
    const { operator, value } = v as PropertyValue
    return value ? [{ field: f.field, operator, value }] : []
  },
  multiselect: (f, v, allOptions) => {
    const selected = v as string[]
    return allOptions
      .filter(o => !selected.includes(o.value))
      .map(o => ({ field: f.field, operator: 'neq' as Operator, value: o.value }))
  },
}

export function buildFilterEntries(
  filter: KanbanFilter,
  value: FilterValue,
  allOptions: { value: string }[] = []
): FilterEntry[] {
  return ENTRY_BUILDERS[filter.type](filter, value, allOptions)
}
